import React, { useEffect, useState, type ReactNode } from 'react';

import { useMerchant } from '@contexts/admin/MerchantContext';
import { useNotification } from '@hooks/admin/useBackgroundJobNotification';
import { BackgroundJobProvider, useBackgroundJob } from '@shared/BackgroundJobManager';

import adminApiFetch from '@services/admin/apiService';
import adminCable from '@/lib/adminCable';

/**
 * Admin Notification Context
 *
 * 用途：在整個 Admin 應用中共享即時通知和背景任務管理功能
 * 使用統一的 BackgroundJobProvider 架構
 */

interface AdminNotificationProviderProps {
  children: ReactNode;
}

/**
 * AdminNotificationProvider - Admin 專用的通知和背景任務 Provider
 *
 * 功能：
 * - 使用統一的 BackgroundJobProvider
 * - 注入 Admin 專用的 Notification Hook 和 API Service
 * - 自動同步未完成任務（當 merchant 載入完成後）
 * - 🔥 根據當前 Merchant ID 使用不同的 storageKey（切換 Merchant 時載入對應的任務）
 * - 提供即時通知資料（包含 job_status 和 new_notification）
 */
export function AdminNotificationProvider({ children }: AdminNotificationProviderProps) {
  const { currentMerchant } = useMerchant();

  // 🔥 根據 Merchant ID 生成 storageKey，確保不同 Merchant 的任務分開儲存
  const storageKey = currentMerchant
    ? `admin_background_jobs_merchant_${currentMerchant.id}`
    : 'admin_background_jobs';

  return (
    <BackgroundJobProvider
      useNotificationHook={useNotification}
      apiFetch={adminApiFetch}
      storageKey={storageKey}
      cableManager={adminCable}
      key={storageKey} // 🔥 重要：當 storageKey 變化時，重新建立 Provider
    >
      <AdminNotificationSyncWrapper>
        {children}
      </AdminNotificationSyncWrapper>
    </BackgroundJobProvider>
  );
}

interface AdminNotificationSyncWrapperProps {
  children: ReactNode;
}

/**
 * AdminNotificationSyncWrapper - 負責同步未完成任務
 *
 * 這個內部組件可以存取 BackgroundJobProvider 的 Context
 */
function AdminNotificationSyncWrapper({ children }: AdminNotificationSyncWrapperProps) {
  const { syncPendingJobs } = useBackgroundJob();
  const { currentMerchant, loading: merchantLoading } = useMerchant();
  const [synced, setSynced] = useState(false);

  // 🔥 等待 merchant 載入完成後，同步未完成的任務狀態
  useEffect(() => {
    if (!merchantLoading && currentMerchant && !synced) {
      syncPendingJobs();
      setSynced(true);
    }
  }, [merchantLoading, currentMerchant, synced, syncPendingJobs]);

  return <>{children}</>;
}

/**
 * useAdminNotification - 使用 Admin 即時通知和背景任務的 Hook
 *
 * 使用範例：
 * ```tsx
 * import { useAdminNotification } from '@contexts/admin/AdminNotificationContext';
 *
 * function MyComponent() {
 *   const { addJob, notificationData } = useAdminNotification();
 *
 *   const handleExport = async () => {
 *     // ... API call
 *     addJob(job_id, 'Export Task');
 *   };
 * }
 * ```
 */
export function useAdminNotification() {
  return useBackgroundJob();
}
